import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Download } from "lucide-react";
import { Button } from "./ui/button";
import { STATS } from "../mock";
import { Reveal } from "./Reveal";

// Every logo sits in the same fixed box (see the marquee below), but source files have very different
// native shapes (a thin wordmark vs. a near-square icon vs. a logo mark padded inside a square canvas),
// so `visualScale` nudges each one to look like it's the same visual size as the others.
const TRUST_BADGES = [
  { name: "AWS Authorized", logo: "/images/aws-logo.png", visualScale: 1.1 },
  { name: "Microsoft Partner", logo: "/images/microsoft-logo.png", visualScale: 1 },
  { name: "Google Cloud Partner", logo: "/images/google-cloud-logo.png", visualScale: 1.6 },
  { name: "Red Hat ATP", logo: "/images/red-hat-logo.png", visualScale: 1.3 },
  { name: "SAP Partner", logo: "/images/sap-logo.svg", visualScale: 1 },
];

const Hero = () => {
  const ctaRef = useRef(null);
  const handleMove = (e) => {
    const el = ctaRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--x", `${e.clientX - r.left}px`);
    el.style.setProperty("--y", `${e.clientY - r.top}px`);
  };

  // Video is only rendered (mounted/loaded/played) at tablet width and up.
  // On mobile it's not just visually hidden — it never downloads or autoplays at all.
  const [showVideo, setShowVideo] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const update = () => setShowVideo(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: "linear-gradient(90deg, #FFFFFF 0%, #FFFFFF 46%, #EAF1FF 54%, #EAF1FF 100%)" }}
    >
      {/* Outer wrapper is full-width (uncapped) so the video can bleed all the way to the true right edge and
          fill any leftover space on wide screens; its height is driven entirely by the capped text row inside
          it, so proportions and the text side are completely unaffected. */}
      <div className="relative lg:flex lg:items-start">
        {/* Large hero visual: bled to the section's true right edge (not capped to max-w-7xl), so it fully
            fills the space up to the edge on any screen width. Height matches the capped row exactly. */}
        <div className="hidden lg:block absolute inset-y-0 right-0 w-[54%] pointer-events-none" data-testid="hero-visual">
          <div className="absolute inset-0">
            {showVideo && (
              <Reveal variant="right" delay={120} className="block w-full h-full">
                <video
                  src="/videos/hero-corporate-animation.mp4"
                  className="w-full h-full object-cover"
                  style={{
                    WebkitMaskImage: "radial-gradient(ellipse 92% 92% at 58% 50%, #000 40%, transparent 90%)",
                    maskImage: "radial-gradient(ellipse 92% 92% at 58% 50%, #000 40%, transparent 90%)",
                    transform: "scale(0.94)",
                    transformOrigin: "top right",
                    objectPosition: "70% center",
                  }}
                  aria-hidden="true"
                  autoPlay
                  loop
                  muted
                  playsInline
                  disablePictureInPicture
                  data-testid="hero-video"
                />
              </Reveal>
            )}
            {/* Soft wash that dissolves the video's left edge into the banner background so there's no visible seam */}
            <div className="absolute inset-y-0 left-0 w-[36%] bg-gradient-to-r from-white from-10% via-white/70 via-30% to-transparent"/>
          </div>
        </div>
        {/* Mini accent badge: ALWAYS rendered (no hidden/breakpoint toggling), just repositions responsively,
            so it can never be missing at any screen width. */}
        <div className="absolute top-4 right-4 lg:top-8 lg:right-8 z-30 bg-[#15348C] text-white rounded-2xl shadow-2xl px-5 py-3 border-2 border-white">
          <div className="text-xl font-bold text-[#8BD835] leading-none">250+</div>
          <div className="text-[10px] text-gray-200 leading-tight mt-1">Enterprise<br />clients</div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 lg:px-0 lg:flex lg:items-start lg:aspect-[920/420] w-full">
        <div className="relative pt-8 pb-14 md:pb-16 lg:pb-4 w-full">
          <div className="lg:max-w-[46%] pl-3 sm:pl-4 lg:pl-6">
            <Reveal variant="up">
              <div className="inline-flex items-center gap-2 bg-white border border-[#6BBE32]/30 shadow-sm px-3 py-1.5 rounded-full text-xs font-semibold text-[#4F9622] mb-5 sg-pulse">
                <Sparkles size={14}/> #1 Authorized Training &amp; Certification Partner
              </div>
            </Reveal>
            <Reveal variant="up" delay={100}>
              <h1 className="text-[#15348C] tracking-tight">
                Future-Ready <span className="text-[#8BD835]">AI &amp; Cloud Training</span><br/>
                for Today’s Enterprises
              </h1>
            </Reveal>
            <Reveal variant="up" delay={200}>
              <p className="mt-5 text-gray-600 leading-relaxed" style={{fontSize:"1.075rem"}}>
                <strong className="text-[#15348C]">250+ enterprise clients</strong> trust SkillsGain to upskill <strong className="text-[#15348C]">4,000+ professionals</strong> across <strong className="text-[#15348C]">30+ emerging technology domains</strong> and <strong className="text-[#15348C]">200+ corporate learning programs</strong>.
              </p>
            </Reveal>

            <Reveal variant="up" delay={280}>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link to="/offerings/all">
                  <Button ref={ctaRef} onMouseMove={handleMove}
                    className="sg-btn-glow bg-[#15348C] hover:bg-[#0A1F5C] text-white font-semibold px-6 h-12 rounded-md shadow-lg shadow-[#15348C]/30">
                    Explore All Offerings <ArrowRight size={17} className="ml-1"/>
                  </Button>
                </Link>
                <a href="/SkillsGain-Course-Catalogue.pdf" download data-testid="hero-download-catalogue">
                  <Button className="sg-btn-glow bg-[#8BD835] hover:bg-[#6BBE32] text-white font-semibold px-6 h-12 rounded-md shadow-lg shadow-[#8BD835]/30">
                    <Download size={17} className="mr-1"/> Download Catalogue
                  </Button>
                </a>
              </div>
            </Reveal>

            {/* Trust logo marquee: confined to the left/text side of the banner only. */}
            <Reveal variant="up" delay={360}>
              <div className="mt-8 lg:mt-6 w-full sg-marquee-mask">
                <div className="sg-marquee-track fast items-center">
                  {[...TRUST_BADGES, ...TRUST_BADGES].map((b, i) => (
                    <div key={`${b.name}-${i}`} className="w-16 h-16 flex items-center justify-center shrink-0 overflow-visible">
                      <img
                        src={b.logo}
                        alt={b.name}
                        title={b.name}
                        className="w-full h-full object-contain"
                        style={{ transform: `scale(${b.visualScale})` }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          {/* Tablet-only visual (md–lg): contained below the text, same blend treatment.
              Hidden entirely on mobile (<md) — not rendered at all, so it never loads or plays there. */}
          {showVideo && (
            <Reveal variant="up" delay={120}>
              <div className="hidden md:block lg:hidden relative mt-10 w-full" data-testid="hero-visual-tablet">
                <video
                  src="/videos/hero-corporate-animation.mp4"
                  className="w-full h-auto object-contain"
                  style={{
                    WebkitMaskImage: "radial-gradient(ellipse 82% 82% at center, #000 60%, transparent 100%)",
                    maskImage: "radial-gradient(ellipse 82% 82% at center, #000 60%, transparent 100%)",
                  }}
                  aria-hidden="true"
                  autoPlay
                  loop
                  muted
                  playsInline
                  disablePictureInPicture
                />
              </div>
            </Reveal>
          )}
        </div>
        </div>
      </div>

      <Reveal>
        <div className="relative max-w-7xl mx-auto px-4 pb-10">
          <div
            className="rounded-2xl shadow-xl border border-gray-100 grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-100"
            style={{ background: "linear-gradient(110deg, #F8FAFF 0%, #EEF3FF 65%, #FFF4EC 100%)" }}
          >
            {STATS.map((s) => (
              <div key={s.label} className="p-6 text-center group">
                <div className="text-3xl lg:text-4xl font-bold text-[#15348C] group-hover:text-[#6BBE32] transition-colors">{s.value}</div>
                <div className="text-sm text-gray-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </section>
  );
};

export default Hero;
